import { useEffect, useCallback } from 'react';
import { create } from 'zustand';

// Define the store types
interface VideoPlayerStore {
  videoElements: Record<string, HTMLMediaElement | null>;
  playerHeights: Record<string, number>;
  playerWidths: Record<string, number>;
  videoDurations: Record<string, number>;
  seekTo: (id: string, time: number) => void;
  registerPlayer: (id: string, element: HTMLMediaElement | null) => void;
  unregisterPlayer: (id: string) => void;
  setPlayerHeight: (id: string, height: number) => void;
  setPlayerWidth: (id: string, width: number) => void;
  setVideoDuration: (id: string, duration: number) => void;
}

// Create the store
export const useVideoPlayerStore = create<VideoPlayerStore>((set) => ({
  videoElements: {},
  playerHeights: {},
  playerWidths: {},
  videoDurations: {},
  seekTo: (id, time) => {
    set((state) => {
      const element = state.videoElements[id];
      // console.log('time', time);

      if (element && Number.isFinite(time)) {
        const duration = state.videoDurations[id];
        element.currentTime = time;

        // Se stiamo cercando di spostarci molto vicino alla fine, simula l'evento 'ended'
        // che farà apparire la schermata finale anche se il video è in pausa
        if (duration && time >= duration - 0.5) {
          // console.log('Seeking to end of video, dispatching ended event');
          element.dispatchEvent(new Event('ended'));
        }
      }
      return state;
    });
  },
  registerPlayer: (id, element) => {
    set((state) => ({
      videoElements: {
        ...state.videoElements,
        [id]: element,
      },
    }));
  },
  unregisterPlayer: (id) => {
    set((state) => {
      const { [id]: _, ...rest } = state.videoElements;
      const { [id]: __, ...restHeights } = state.playerHeights;
      const { [id]: ___, ...restDurations } = state.videoDurations;
      const { [id]: ____, ...restWidths } = state.playerWidths;
      return {
        videoElements: rest,
        playerHeights: restHeights,
        playerWidths: restWidths,
        videoDurations: restDurations,
      };
    });
  },
  setPlayerHeight: (id, height) => {
    if (height > 0) {
      set((state) => {
        return {
          playerHeights: {
            ...state.playerHeights,
            [id]: height,
          },
        };
      });
    }
  },
  setPlayerWidth: (id, width) => {
    if (width > 0) {
      set((state) => {
        return {
          playerWidths: {
            ...state.playerWidths,
            [id]: width,
          },
        };
      });
    }
  },
  setVideoDuration: (id, duration) => {
    set((state) => ({
      videoDurations: {
        ...state.videoDurations,
        [id]: duration,
      },
    }));
  },
}));

// Hook for external components to control the video player
export const useVideoPlayerControl = (playerId: string) => {
  const { seekTo, playerHeights, playerWidths, videoDurations } = useVideoPlayerStore();

  const seekToTime = useCallback(
    (time: number) => {
      seekTo(playerId, time);
    },
    [seekTo, playerId],
  );

  return {
    seekToTime,
    playerHeight: playerHeights[playerId] || 0,
    playerWidth: playerWidths[playerId] || 0,
    duration: videoDurations[playerId] || 0,
  };
};

// Hook for the VideoPlayer component to register itself
export const useVideoPlayerRegistration = (
  playerId: string,
  videoElement: HTMLMediaElement | null,
  height: number,
  width?: number,
) => {
  const { registerPlayer, unregisterPlayer, setPlayerHeight, setPlayerWidth, setVideoDuration } =
    useVideoPlayerStore();

  // Register the player when the component mounts
  useEffect(() => {
    registerPlayer(playerId, videoElement);

    // If we have a video element, listen for duration change
    if (videoElement) {
      const handleDurationChange = () => {
        if (videoElement && Number.isFinite(videoElement.duration)) {
          setVideoDuration(playerId, videoElement.duration);
        }
      };

      // Set initial duration if already available
      if (Number.isFinite(videoElement.duration) && videoElement.duration > 0) {
        setVideoDuration(playerId, videoElement.duration);
      }

      // Add event listener for duration change
      videoElement.addEventListener('durationchange', handleDurationChange);
      videoElement.addEventListener('loadedmetadata', handleDurationChange);

      // Also track resize to update dimensions
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          if (height > 0) {
            setPlayerHeight(playerId, height);
          }
          if (width > 0) {
            setPlayerWidth(playerId, width);
          }
        }
      });

      // Start observing the video element
      resizeObserver.observe(videoElement);

      return () => {
        videoElement.removeEventListener('durationchange', handleDurationChange);
        videoElement.removeEventListener('loadedmetadata', handleDurationChange);
        resizeObserver.disconnect();
        unregisterPlayer(playerId);
      };
    }

    return () => {
      unregisterPlayer(playerId);
    };
  }, [playerId, videoElement, registerPlayer, unregisterPlayer, setVideoDuration, setPlayerWidth]);

  // Update the height in the store
  useEffect(() => {
    if (height > 0) {
      setPlayerHeight(playerId, height);
    }
  }, [playerId, height, setPlayerHeight]);

  // Update the width in the store if provided
  useEffect(() => {
    if (width && width > 0) {
      setPlayerWidth(playerId, width);
    }
  }, [playerId, width, setPlayerWidth]);
};
